import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type { SlideElement } from '@/shared/types/slide';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface SlideEditorState {
  currentSlideId: string | null;
  selectedElementIds: string[];
  activeToolType: 'select' | 'text' | 'shape' | 'image';
  activeShapeType: 'rectangle' | 'circle' | 'line';
  zoom: number;
  clipboard: SlideElement[] | null;
}

const initialState: SlideEditorState = {
  currentSlideId: null,
  selectedElementIds: [],
  activeToolType: 'select',
  activeShapeType: 'rectangle',
  zoom: 100,
  clipboard: null,
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const slideEditorSlice = createSlice({
  name: 'slideEditor',
  initialState,
  reducers: {
    setCurrentSlide(state, action: PayloadAction<string | null>) {
      state.currentSlideId = action.payload;
      state.selectedElementIds = [];
    },

    selectElements(state, action: PayloadAction<string[]>) {
      state.selectedElementIds = action.payload;
    },

    clearSelection(state) {
      state.selectedElementIds = [];
    },

    setActiveTool(state, action: PayloadAction<SlideEditorState['activeToolType']>) {
      state.activeToolType = action.payload;
    },

    setActiveShapeType(state, action: PayloadAction<SlideEditorState['activeShapeType']>) {
      state.activeShapeType = action.payload;
    },

    setZoom(state, action: PayloadAction<number>) {
      state.zoom = Math.max(25, Math.min(200, action.payload));
    },

    copyToClipboard(state, action: PayloadAction<SlideElement[]>) {
      state.clipboard = action.payload;
    },

    clearClipboard(state) {
      state.clipboard = null;
    },

    /** Reset all editor state — called on page unmount. */
    resetEditor() {
      return initialState;
    },
  },
});

export const {
  setCurrentSlide,
  selectElements,
  clearSelection,
  setActiveTool,
  setActiveShapeType,
  setZoom,
  copyToClipboard,
  clearClipboard,
  resetEditor,
} = slideEditorSlice.actions;

export default slideEditorSlice.reducer;
